// API 供应商类型
export interface ApiProvider {
  id: string;
  name: string;
  baseUrl: string;
  apiKey: string;
  providerType: ProviderType;
  models: string[];
  defaultModel?: string;
  config: ProviderConfig;
  createdAt: string;
  updatedAt: string;
}

// 供应商类型枚举
export enum ProviderType {
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
  GOOGLE_AI = 'google-ai',
  AZURE_OPENAI = 'azure-openai',
  CUSTOM = 'custom',
}

// 供应商配置
export interface ProviderConfig {
  temperature?: number;
  maxTokens?: number;
  topP?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  timeout?: number;
  headers?: Record<string, string>;
  additionalParams?: Record<string, any>;
}

// 识别的配置项
export interface ParsedConfig {
  baseUrl?: string;
  apiKey?: string;
  model?: string;
  providerType?: ProviderType;
  additionalConfig?: Record<string, any>;
  confidence: number; // 识别置信度 0-1
}

// 对话消息
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  providerId?: string;
  model?: string;
  tokens?: number;
}

// 对话会话
export interface ChatSession {
  id: string;
  name: string;
  providerId: string;
  model: string;
  messages: ChatMessage[];
  config: ChatConfig;
  tags: string[];
  summary?: string;
  messageCount: number;
  tokenCount: number;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
}

// 对话配置
export interface ChatConfig {
  temperature: number;
  maxTokens: number;
  topP: number;
  frequencyPenalty: number;
  presencePenalty: number;
  stream: boolean;
}

// 默认对话模板
export interface ChatTemplate {
  id: string;
  name: string;
  description: string;
  content: string;
  category: 'greeting' | 'code' | 'summary' | 'creative' | 'test';
  tags: string[];
}

// 模型补全规则
export interface ModelCompletionRule {
  prefix: string;
  completions: string[];
  description: string;
}

// 请求格式预览
export interface RequestFormat {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers: Record<string, string>;
  body?: any;
  example: string;
}

// 应用状态
export interface AppState {
  providers: ApiProvider[];
  currentProviderId: string | null;
  sessions: ChatSession[];
  currentSessionId: string | null;
  templates: ChatTemplate[];
  ui: {
    sidebarCollapsed: boolean;
    activeTab: 'config' | 'providers' | 'chat' | 'history';
    theme: 'light' | 'dark';
  };
}

// 错误类型
export interface ApiError {
  code: string;
  message: string;
  details?: any;
  timestamp: string;
}

// 分页参数
export interface PaginationParams {
  page: number;
  pageSize: number;
  total: number;
}

// 分页结果
export interface PaginatedResult<T> {
  items: T[];
  pagination: PaginationParams;
}

// 对话搜索参数
export interface SessionSearchParams {
  query?: string;
  providerId?: string;
  startDate?: string;
  endDate?: string;
  tags?: string[];
  page?: number;
  pageSize?: number;
}

// 对话统计信息
export interface SessionStats {
  totalSessions: number;
  totalMessages: number;
  totalTokens: number;
  byProvider: Record<string, number>;
  byDate: Record<string, number>;
  recentActivity: Array<{
    date: string;
    sessions: number;
    messages: number;
  }>;
}

// 数据导出格式
export interface DataExport {
  version: string;
  exportedAt: string;
  providers: ApiProvider[];
  sessions: ChatSession[];
  templates: ChatTemplate[];
}

// 导入结果
export interface ImportResult {
  success: boolean;
  importedCount: {
    providers: number;
    sessions: number;
    templates: number;
  };
  errors: string[];
  warnings: string[];
}